import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { BottomSheet, Button, Checkbox, Chip, Dialog, Notice } from '@sauda/ui/kit';
import { ROLES, type Role } from '@sauda/shared';
import { ApiError, api } from '../../lib/api.js';
import type { Branch, Member } from '../../lib/types.js';

/**
 * A member: change the role (after a confirmation), move to another branch, or start removing.
 * It is the body of a sheet on a phone and of the detail pane on a wide window.
 */
export function MemberPanel({
  member,
  branches,
  canManage,
  onClose,
  onRemove,
}: {
  member: Member | null;
  branches: Branch[];
  canManage: boolean;
  onClose: () => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const cache = useQueryClient();
  const [role, setRole] = useState<Role | null>(null);
  const [branchId, setBranchId] = useState<string | null>(null);
  const [moveRecords, setMoveRecords] = useState(false);
  const [confirmRole, setConfirmRole] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const records = useQuery({
    queryKey: ['member-records', member?.id],
    queryFn: () => api<{ total: number }>(`/members/${member?.id ?? ''}/records`),
    enabled: member !== null,
  });
  const refresh = async () => {
    await cache.invalidateQueries({ queryKey: ['members'] });
  };
  const fail = (e: unknown) =>
    setError(
      e instanceof ApiError && e.code === 'LAST_OWNER'
        ? t('team.lastOwner')
        : e instanceof ApiError && e.code === 'OPEN_RECORDS'
          ? t('team.openRecords')
          : t('team.generic'),
    );
  const changeRole = useMutation({
    mutationFn: (next: Role) =>
      api(`/members/${member?.id ?? ''}/role`, { method: 'PATCH', body: { role: next } }),
    onSuccess: async () => {
      setConfirmRole(false);
      setError(null);
      await refresh();
      done();
    },
    onError: (e) => {
      setConfirmRole(false);
      fail(e);
    },
  });
  const move = useMutation({
    mutationFn: (to: string) =>
      api(`/members/${member?.id ?? ''}/branch`, {
        method: 'PATCH',
        body: { branchId: to, moveRecords },
      }),
    onSuccess: async () => {
      setError(null);
      await refresh();
      done();
    },
    onError: fail,
  });
  const done = () => {
    setRole(null);
    setBranchId(null);
    setMoveRecords(false);
    onClose();
  };
  if (member === null) return null;
  const pickedRole = role ?? member.role;
  const pickedBranch = branchId ?? member.branchId;
  return (
    <>
      <div className="form-stack">
        <p className="k-t-body k-muted">
          {member.phone} · {t(`roles.${member.role}`)}
        </p>
        {error === null ? null : <Notice tone="error">{error}</Notice>}
        {canManage ? (
          <>
            <div role="group" aria-label={t('team.role')} className="form-group">
              <p className="k-t-label">{t('team.role')}</p>
              <div className="chips-row">
                {ROLES.map((r) => (
                  <Chip key={r} selected={pickedRole === r} onClick={() => setRole(r)}>
                    {t(`roles.${r}`)}
                  </Chip>
                ))}
              </div>
              <div>
                <Button
                  variant="tonal"
                  disabled={pickedRole === member.role}
                  onClick={() => setConfirmRole(true)}
                >
                  {t('team.saveRole')}
                </Button>
              </div>
            </div>
            <div role="group" aria-label={t('team.branch')} className="form-group">
              <p className="k-t-label">{t('team.branch')}</p>
              <div className="chips-row">
                {branches.map((b) => (
                  <Chip
                    key={b.id}
                    selected={pickedBranch === b.id}
                    onClick={() => setBranchId(b.id)}
                  >
                    {b.name}
                  </Chip>
                ))}
              </div>
              <Checkbox
                label={t('team.takeRecords', { count: records.data?.total ?? 0 })}
                checked={moveRecords}
                onChange={(e) => setMoveRecords(e.target.checked)}
              />
              <div>
                <Button
                  variant="tonal"
                  loading={move.isPending}
                  disabled={pickedBranch === member.branchId}
                  onClick={() => move.mutate(pickedBranch)}
                >
                  {t('team.saveBranch')}
                </Button>
              </div>
            </div>
            <p className="k-t-body-sm k-muted">
              {t('team.openRecordsCount', { count: records.data?.total ?? 0 })}
            </p>
            <Button variant="outlined" destructive onClick={onRemove}>
              {t('team.deactivate')}
            </Button>
          </>
        ) : null}
      </div>
      <Dialog
        open={confirmRole}
        onClose={() => setConfirmRole(false)}
        title={t('team.confirmRoleTitle')}
        actions={
          <>
            <Button variant="text" onClick={() => setConfirmRole(false)}>
              {t('app.cancel')}
            </Button>
            <Button loading={changeRole.isPending} onClick={() => changeRole.mutate(pickedRole)}>
              {t('team.saveRole')}
            </Button>
          </>
        }
      >
        {t('team.confirmRoleBody', {
          name: member.name ?? member.phone,
          role: t(`roles.${pickedRole}`),
        })}
      </Dialog>
    </>
  );
}

/** The member panel in a bottom sheet (a side sheet from 840 px). */
export function MemberSheet(props: Parameters<typeof MemberPanel>[0]) {
  const { t } = useTranslation();
  const { member, onClose } = props;
  if (member === null) return null;
  return (
    <BottomSheet
      open
      onClose={onClose}
      label={member.name ?? member.phone}
      title={member.name ?? member.phone}
      closeLabel={t('app.close')}
    >
      <MemberPanel {...props} />
    </BottomSheet>
  );
}
